import { useEffect, useMemo, useState } from 'react';
import { Plus, Search, Building2, KeyRound, Receipt, Check, Save } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/Components/ui/Button';
import { Card, CardHead } from '@/Components/ui/Card';
import { Input } from '@/Components/ui/Input';
import { Select } from '@/Components/ui/Select';
import { Textarea } from '@/Components/ui/Textarea';
import { Checkbox } from '@/Components/ui/Checkbox';
import { IconButton } from '@/Components/ui/IconButton';
import { PageHeader } from '@/Components/data/PageHeader';
import { EmptyState } from '@/Components/data/EmptyState';
import { StatusBadge } from '@/Components/ui/Badge';
import type { Tone } from '@/Components/ui/types';
import { TableActions } from '@/Components/ui/TableActions';
import DecimalInput from '@/Components/forms/DecimalInput';
import { Field } from '@/Components/forms/Field';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/Components/ui/Dialog';
import empresaService, { type Empresa, type EmpresaInput } from '@/services/empresaAdmin';
import historicoPagamentoService, { type HistoricoPagamento, type StatusPagamento } from '@/services/historicoPagamento';
import { getUuidCandidate } from '@/utils/uuid';

const ESTADOS_BRASIL = [
  'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA',
  'MT', 'MS', 'MG', 'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN',
  'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO',
];

const FORM_VAZIO: EmpresaInput = {
  social_name: '',
  trade_name: '',
  cnpj: '',
  address: '',
  number: '',
  complement: '',
  neighborhood: '',
  city: '',
  state: '',
  zip_code: '',
  phone: '',
  email: '',
  is_active: true,
  has_sped: false,
  has_dfe: false,
};

const STATUS_PAGAMENTO_LABELS: Record<StatusPagamento, string> = {
  PENDENTE: 'Pendente',
  PAGO: 'Pago',
  ATRASADO: 'Atrasado',
  CANCELADO: 'Cancelado',
};

const STATUS_PAGAMENTO_TONES: Record<StatusPagamento, Tone> = {
  PENDENTE: 'warning',
  PAGO: 'success',
  ATRASADO: 'danger',
  CANCELADO: 'neutral',
};

export default function AdminEmpresas() {
  const navigate = useNavigate();
  const [empresas, setEmpresas] = useState<Empresa[]>([]);
  const [loading, setLoading] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [busca, setBusca] = useState('');

  const [dialog, setDialog] = useState(false);
  const [dialogLicenca, setDialogLicenca] = useState(false);
  const [dialogPagamentos, setDialogPagamentos] = useState(false);
  const [dialogNovoPagamento, setDialogNovoPagamento] = useState(false);
  const [dialogConfirmarPagamento, setDialogConfirmarPagamento] = useState(false);

  const [form, setForm] = useState<EmpresaInput & { id?: string }>(FORM_VAZIO);
  const [empresaSelecionada, setEmpresaSelecionada] = useState<Empresa | null>(null);

  const [licencaForm, setLicencaForm] = useState({
    licenca_ativa: false,
    data_vencimento_licenca: '',
    valor_mensalidade: 0,
    observacoes_licenca: '',
  });

  const [pagamentos, setPagamentos] = useState<HistoricoPagamento[]>([]);
  const [novoPagamento, setNovoPagamento] = useState({
    mes_referencia: '',
    valor: 0,
    data_vencimento: '',
    forma_pagamento: '',
    observacoes: '',
    status: 'PENDENTE' as StatusPagamento,
  });
  const [pagamentoConfirmar, setPagamentoConfirmar] = useState<{ id: string; data_pagamento: string; forma_pagamento: string; observacoes: string } | null>(null);

  const carregar = async () => {
    setLoading(true);
    try {
      const data = await empresaService.listar();
      setEmpresas(data);
    } catch (error) {
      console.error('Erro ao carregar empresas', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregar();
  }, []);

  const filtradas = useMemo(() => {
    if (!busca) return empresas;
    const termo = busca.toLowerCase();
    return empresas.filter(
      (e) =>
        e.cnpj?.toLowerCase().includes(termo) ||
        e.social_name?.toLowerCase().includes(termo) ||
        e.trade_name?.toLowerCase().includes(termo)
    );
  }, [empresas, busca]);

  const getCidadeUF = (item: Empresa) => {
    if (item.city && item.state) return `${item.city}/${item.state}`;
    return item.city || item.state || '-';
  };

  const abrirNova = () => {
    setForm(FORM_VAZIO);
    setDialog(true);
  };

  const editar = (item: Empresa) => {
    setForm({ ...item });
    setDialog(true);
  };

  const salvar = async () => {
    if (!form.social_name || !form.cnpj) return;
    setSalvando(true);
    try {
      if (form.id) {
        await empresaService.atualizar(form.id, form);
      } else {
        await empresaService.criar(form);
      }
      setDialog(false);
      await carregar();
    } catch (error) {
      console.error('Erro ao salvar empresa', error);
    } finally {
      setSalvando(false);
    }
  };

  const deletar = async (item: Empresa) => {
    try {
      await empresaService.excluir(item.id);
      await carregar();
    } catch (error) {
      console.error('Erro ao excluir empresa', error);
      throw error;
    }
  };

  const abrirLicenca = (item: Empresa) => {
    setEmpresaSelecionada(item);
    setLicencaForm({
      licenca_ativa: item.licenca_ativa,
      data_vencimento_licenca: item.data_vencimento_licenca?.slice(0, 10) || '',
      valor_mensalidade: item.valor_mensalidade || 0,
      observacoes_licenca: item.observacoes_licenca || '',
    });
    setDialogLicenca(true);
  };

  const salvarLicenca = async () => {
    if (!empresaSelecionada) return;
    try {
      if (licencaForm.licenca_ativa) {
        await empresaService.ativarLicenca(empresaSelecionada.id, {
          data_vencimento: licencaForm.data_vencimento_licenca,
          valor_mensalidade: licencaForm.valor_mensalidade,
          observacoes: licencaForm.observacoes_licenca,
        });
      } else {
        await empresaService.desativarLicenca(empresaSelecionada.id, {
          motivo: licencaForm.observacoes_licenca,
        });
      }
      setDialogLicenca(false);
      await carregar();
    } catch (error) {
      console.error('Erro ao gerenciar licença', error);
    }
  };

  const verHistoricoPagamentos = async (item: Empresa) => {
    try {
      const data = await empresaService.historicoPagamentos(item.id);
      setPagamentos(data);
      setEmpresaSelecionada(item);
      setDialogPagamentos(true);
    } catch (error) {
      console.error('Erro ao carregar pagamentos', error);
    }
  };

  const abrirNovoPagamento = () => {
    if (!empresaSelecionada) return;
    setNovoPagamento({
      mes_referencia: new Date().toISOString().substring(0, 7),
      valor: empresaSelecionada.valor_mensalidade || 0,
      data_vencimento: '',
      forma_pagamento: '',
      observacoes: '',
      status: 'PENDENTE',
    });
    setDialogNovoPagamento(true);
  };

  const salvarNovoPagamento = async () => {
    if (!empresaSelecionada) return;
    const empresaId = getUuidCandidate(empresaSelecionada);
    if (!empresaId) return;
    try {
      await historicoPagamentoService.criar({
        empresa_id: empresaId,
        mes_referencia: `${novoPagamento.mes_referencia}-01`,
        valor: novoPagamento.valor,
        data_vencimento: novoPagamento.data_vencimento,
        forma_pagamento: novoPagamento.forma_pagamento,
        observacoes: novoPagamento.observacoes,
        status: novoPagamento.status,
      });
      setDialogNovoPagamento(false);
      await verHistoricoPagamentos(empresaSelecionada);
    } catch (error) {
      console.error('Erro ao adicionar pagamento', error);
    }
  };

  const abrirConfirmarPagamento = (pagamento: HistoricoPagamento) => {
    setPagamentoConfirmar({
      id: pagamento.id,
      data_pagamento: new Date().toISOString().substring(0, 10),
      forma_pagamento: pagamento.forma_pagamento || '',
      observacoes: pagamento.observacoes || '',
    });
    setDialogConfirmarPagamento(true);
  };

  const confirmarPagamentoFinal = async () => {
    if (!pagamentoConfirmar || !empresaSelecionada) return;
    try {
      await historicoPagamentoService.confirmarPagamento(pagamentoConfirmar.id, {
        data_pagamento: pagamentoConfirmar.data_pagamento,
        forma_pagamento: pagamentoConfirmar.forma_pagamento,
        observacoes: pagamentoConfirmar.observacoes,
      });
      setDialogConfirmarPagamento(false);
      await verHistoricoPagamentos(empresaSelecionada);
      await carregar();
    } catch (error) {
      console.error('Erro ao confirmar pagamento', error);
    }
  };

  const getStatusLicencaTone = (item: Empresa): Tone => {
    if (!item.licenca_ativa) return 'danger';
    if (item.data_vencimento_licenca && new Date(item.data_vencimento_licenca) < new Date()) return 'warning';
    return 'success';
  };

  return (
    <div className="nx-page">
      <PageHeader
        icon={<Building2 size={21} strokeWidth={2} />}
        title="Empresas"
        description="Administração das empresas (tenants) cadastradas no sistema."
        actions={
          <Button type="button" onClick={() => navigate('/admin/empresas/novo')}>
            <Plus size={16} strokeWidth={2.2} />
            Nova Empresa
          </Button>
        }
      />

      <Card className="nx-page-card nx-sngpc-listing-card">
        <CardHead
          title="Listagem"
          actions={
            <div className="nx-card-head-actions">
              <div className="nx-search-field">
                <Search size={15} strokeWidth={2} aria-hidden />
                <input
                  value={busca}
                  onChange={(e) => setBusca(e.target.value)}
                  placeholder="Buscar empresa..."
                />
              </div>
            </div>
          }
        />

        {!loading && filtradas.length === 0 ? (
          <EmptyState message="Nenhuma empresa encontrada." />
        ) : (
          <div className="nx-table-wrap">
            <table className="nx-table">
              <thead>
                <tr>
                  <th>CNPJ</th>
                  <th>Razão Social</th>
                  <th>Nome Fantasia</th>
                  <th>Cidade/UF</th>
                  <th>Licença</th>
                  <th>Vencimento</th>
                  <th>Status</th>
                  <th className="nx-col-acoes">Ações</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={8} className="nx-table-loading">Carregando…</td>
                  </tr>
                ) : (
                  filtradas.map((item) => (
                    <tr key={item.id}>
                      <td>{item.cnpj}</td>
                      <td>{item.social_name}</td>
                      <td>{item.trade_name || '-'}</td>
                      <td>{getCidadeUF(item)}</td>
                      <td>
                        <StatusBadge label={item.licenca_ativa ? 'Ativa' : 'Inativa'} tone={getStatusLicencaTone(item)} />
                      </td>
                      <td>{item.data_vencimento_licenca ? new Date(item.data_vencimento_licenca).toLocaleDateString('pt-BR') : '-'}</td>
                      <td>
                        <StatusBadge label={item.is_active ? 'Ativo' : 'Inativo'} tone={item.is_active ? 'success' : 'danger'} />
                      </td>
                      <td className="nx-col-acoes">
                        <div className="nx-table-actions-group nx-table-actions-group--buttons">
                          <IconButton label="Gerenciar licença" onClick={() => abrirLicenca(item)}>
                            <KeyRound size={15} strokeWidth={1.9} aria-hidden />
                          </IconButton>
                          <IconButton label="Histórico de pagamentos" onClick={() => verHistoricoPagamentos(item)}>
                            <Receipt size={15} strokeWidth={1.9} aria-hidden />
                          </IconButton>
                          <TableActions
                            onEdit={() => navigate(`/admin/empresas/${item.id}/editar`)}
                            onDelete={() => deletar(item)}
                            deleteTitle="Excluir empresa"
                            deleteDescription={`Tem certeza que deseja excluir a empresa "${item.social_name}"? Esta ação não pode ser desfeita.`}
                          />
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Dialog Criar/Editar Empresa */}
      <Dialog open={dialog} onOpenChange={(next) => !salvando && setDialog(next)}>
        <DialogContent className="nexus-dialog--wide nx-sngpc-edit-dialog">
          <form onSubmit={(event) => { event.preventDefault(); void salvar(); }}>
          <DialogHeader>
            <DialogTitle>{form.id ? 'Editar Empresa' : 'Nova Empresa'}</DialogTitle>
            <DialogDescription>
              {form.id ? 'Atualize os dados cadastrais da empresa.' : 'Preencha os dados para incluir uma nova empresa.'}
            </DialogDescription>
          </DialogHeader>

          <div className="nexus-dialog-body">
            <div className="nx-form-grid">
              <Field label="Razão Social *" htmlFor="empresa-social-name" className="nx-sngpc-field--full">
                <Input
                  id="empresa-social-name"
                  value={form.social_name || ''}
                  onChange={(e) => setForm({ ...form, social_name: e.target.value })}
                  required
                />
              </Field>
              <Field label="Nome Fantasia" htmlFor="empresa-trade-name">
                <Input
                  id="empresa-trade-name"
                  value={form.trade_name || ''}
                  onChange={(e) => setForm({ ...form, trade_name: e.target.value })}
                />
              </Field>
              <Field label="CNPJ *" htmlFor="empresa-cnpj">
                <Input
                  id="empresa-cnpj"
                  value={form.cnpj || ''}
                  onChange={(e) => setForm({ ...form, cnpj: e.target.value })}
                  required
                />
              </Field>
              <Field label="Telefone" htmlFor="empresa-phone">
                <Input
                  id="empresa-phone"
                  value={form.phone || ''}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                />
              </Field>
              <Field label="E-mail" htmlFor="empresa-email">
                <Input
                  id="empresa-email"
                  type="email"
                  value={form.email || ''}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                />
              </Field>
              <Field label="Endereço" htmlFor="empresa-address">
                <Input
                  id="empresa-address"
                  value={form.address || ''}
                  onChange={(e) => setForm({ ...form, address: e.target.value })}
                />
              </Field>
              <Field label="Número" htmlFor="empresa-number">
                <Input
                  id="empresa-number"
                  value={form.number || ''}
                  onChange={(e) => setForm({ ...form, number: e.target.value })}
                />
              </Field>
              <Field label="Complemento" htmlFor="empresa-complement">
                <Input
                  id="empresa-complement"
                  value={form.complement || ''}
                  onChange={(e) => setForm({ ...form, complement: e.target.value })}
                />
              </Field>
              <Field label="Bairro" htmlFor="empresa-neighborhood">
                <Input
                  id="empresa-neighborhood"
                  value={form.neighborhood || ''}
                  onChange={(e) => setForm({ ...form, neighborhood: e.target.value })}
                />
              </Field>
              <Field label="CEP" htmlFor="empresa-zip-code">
                <Input
                  id="empresa-zip-code"
                  value={form.zip_code || ''}
                  onChange={(e) => setForm({ ...form, zip_code: e.target.value })}
                />
              </Field>
              <Field label="Cidade" htmlFor="empresa-city">
                <Input
                  id="empresa-city"
                  value={form.city || ''}
                  onChange={(e) => setForm({ ...form, city: e.target.value })}
                />
              </Field>
              <Field label="Estado" htmlFor="empresa-state">
                <Select
                  id="empresa-state"
                  value={form.state || ''}
                  onChange={(e) => setForm({ ...form, state: e.target.value })}
                >
                  <option value="">Selecione</option>
                  {ESTADOS_BRASIL.map((uf) => <option key={uf} value={uf}>{uf}</option>)}
                </Select>
              </Field>
            </div>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', marginTop: '1rem' }}>
              <label className="nx-sngpc-checkbox-field">
                <Checkbox checked={!!form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />
                <span>Ativa</span>
              </label>
              <label className="nx-sngpc-checkbox-field">
                <Checkbox checked={!!form.has_sped} onChange={(e) => setForm({ ...form, has_sped: e.target.checked })} />
                <span>Módulo SPED</span>
              </label>
              <label className="nx-sngpc-checkbox-field">
                <Checkbox checked={!!form.has_dfe} onChange={(e) => setForm({ ...form, has_dfe: e.target.checked })} />
                <span>Módulo DF-e</span>
              </label>
            </div>
          </div>

          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="ghost" disabled={salvando}>Cancelar</Button>
            </DialogClose>
            <Button type="submit" loading={salvando} loadingText="Salvando...">
              <Save size={16} /> Salvar
            </Button>
          </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog Licença */}
      <Dialog open={dialogLicenca} onOpenChange={setDialogLicenca}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Gerenciar Licença</DialogTitle>
            <DialogDescription>
              {empresaSelecionada?.trade_name || empresaSelecionada?.social_name || ''}
            </DialogDescription>
          </DialogHeader>

          <div className="nx-form-grid">
            <label className="nx-sngpc-checkbox-field">
              <Checkbox
                checked={licencaForm.licenca_ativa}
                onChange={(e) => setLicencaForm({ ...licencaForm, licenca_ativa: e.target.checked })}
              />
              <span>Licença ativa</span>
            </label>
            <Field label="Data de vencimento" htmlFor="licenca-vencimento">
              <Input
                id="licenca-vencimento"
                type="date"
                value={licencaForm.data_vencimento_licenca}
                onChange={(e) => setLicencaForm({ ...licencaForm, data_vencimento_licenca: e.target.value })}
              />
            </Field>
            <Field label="Valor da mensalidade" htmlFor="licenca-valor">
              <DecimalInput
                id="licenca-valor"
                value={licencaForm.valor_mensalidade}
                onValueChange={(value) => setLicencaForm({ ...licencaForm, valor_mensalidade: value ?? 0 })}
              />
            </Field>
            <Field label="Observações" htmlFor="licenca-observacoes" className="nx-sngpc-field--full">
              <Textarea
                id="licenca-observacoes"
                value={licencaForm.observacoes_licenca}
                onChange={(e) => setLicencaForm({ ...licencaForm, observacoes_licenca: e.target.value })}
                rows={3}
              />
            </Field>
          </div>

          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="ghost">Cancelar</Button>
            </DialogClose>
            <Button type="button" onClick={salvarLicenca}>Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Histórico de Pagamentos */}
      <Dialog open={dialogPagamentos} onOpenChange={setDialogPagamentos}>
        <DialogContent className="nexus-dialog--wide">
          <DialogHeader>
            <DialogTitle>Histórico de Pagamentos</DialogTitle>
            <DialogDescription>
              {empresaSelecionada?.trade_name || empresaSelecionada?.social_name || ''}
            </DialogDescription>
          </DialogHeader>

          <div className="nx-table-wrap">
            <table className="nx-table">
              <thead>
                <tr>
                  <th>Mês Ref.</th>
                  <th>Valor</th>
                  <th>Vencimento</th>
                  <th>Status</th>
                  <th className="nx-col-acoes">Ações</th>
                </tr>
              </thead>
              <tbody>
                {pagamentos.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="nx-table-loading">Nenhum pagamento registrado</td>
                  </tr>
                ) : (
                  pagamentos.map((p) => (
                    <tr key={p.id}>
                      <td>{new Date(p.mes_referencia).toLocaleDateString('pt-BR', { month: '2-digit', year: 'numeric' })}</td>
                      <td>R$ {Number(p.valor).toFixed(2)}</td>
                      <td>{new Date(p.data_vencimento).toLocaleDateString('pt-BR')}</td>
                      <td>
                        <StatusBadge label={STATUS_PAGAMENTO_LABELS[p.status]} tone={STATUS_PAGAMENTO_TONES[p.status]} />
                      </td>
                      <td className="nx-col-acoes">
                        {p.status !== 'PAGO' && (
                          <IconButton label="Confirmar pagamento" onClick={() => abrirConfirmarPagamento(p)}>
                            <Check size={15} strokeWidth={1.9} aria-hidden />
                          </IconButton>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="ghost">Fechar</Button>
            </DialogClose>
            <Button type="button" onClick={abrirNovoPagamento}>
              <Plus size={16} strokeWidth={2.2} />
              Novo Pagamento
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Novo Pagamento */}
      <Dialog open={dialogNovoPagamento} onOpenChange={setDialogNovoPagamento}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Novo Pagamento</DialogTitle>
          </DialogHeader>

          <div className="nx-form-grid">
            <Field label="Mês de referência" htmlFor="pagamento-mes">
              <Input
                id="pagamento-mes"
                type="month"
                value={novoPagamento.mes_referencia}
                onChange={(e) => setNovoPagamento({ ...novoPagamento, mes_referencia: e.target.value })}
              />
            </Field>
            <Field label="Valor" htmlFor="pagamento-valor">
              <DecimalInput
                id="pagamento-valor"
                value={novoPagamento.valor}
                onValueChange={(value) => setNovoPagamento({ ...novoPagamento, valor: value ?? 0 })}
              />
            </Field>
            <Field label="Data de vencimento" htmlFor="pagamento-vencimento">
              <Input
                id="pagamento-vencimento"
                type="date"
                value={novoPagamento.data_vencimento}
                onChange={(e) => setNovoPagamento({ ...novoPagamento, data_vencimento: e.target.value })}
              />
            </Field>
            <Field label="Forma de pagamento" htmlFor="pagamento-forma">
              <Input
                id="pagamento-forma"
                value={novoPagamento.forma_pagamento}
                onChange={(e) => setNovoPagamento({ ...novoPagamento, forma_pagamento: e.target.value })}
              />
            </Field>
            <Field label="Observações" htmlFor="pagamento-observacoes" className="nx-sngpc-field--full">
              <Textarea
                id="pagamento-observacoes"
                value={novoPagamento.observacoes}
                onChange={(e) => setNovoPagamento({ ...novoPagamento, observacoes: e.target.value })}
                rows={2}
              />
            </Field>
          </div>

          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="ghost">Cancelar</Button>
            </DialogClose>
            <Button type="button" onClick={salvarNovoPagamento}>Adicionar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Confirmar Pagamento */}
      <Dialog open={dialogConfirmarPagamento} onOpenChange={setDialogConfirmarPagamento}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmar Pagamento</DialogTitle>
            <DialogDescription>
              Ao confirmar, a licença da empresa será renovada automaticamente.
            </DialogDescription>
          </DialogHeader>

          {pagamentoConfirmar && (
            <div className="nx-form-grid">
              <Field label="Data do pagamento" htmlFor="confirmar-data">
                <Input
                  id="confirmar-data"
                  type="date"
                  value={pagamentoConfirmar.data_pagamento}
                  onChange={(e) => setPagamentoConfirmar({ ...pagamentoConfirmar, data_pagamento: e.target.value })}
                />
              </Field>
              <Field label="Forma de pagamento" htmlFor="confirmar-forma">
                <Input
                  id="confirmar-forma"
                  value={pagamentoConfirmar.forma_pagamento}
                  onChange={(e) => setPagamentoConfirmar({ ...pagamentoConfirmar, forma_pagamento: e.target.value })}
                />
              </Field>
              <Field label="Observações" htmlFor="confirmar-observacoes" className="nx-sngpc-field--full">
                <Textarea
                  id="confirmar-observacoes"
                  value={pagamentoConfirmar.observacoes}
                  onChange={(e) => setPagamentoConfirmar({ ...pagamentoConfirmar, observacoes: e.target.value })}
                  rows={2}
                />
              </Field>
            </div>
          )}

          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="ghost">Cancelar</Button>
            </DialogClose>
            <Button type="button" onClick={confirmarPagamentoFinal}>Confirmar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
